import React, { useEffect, useState } from "react";
import { ShieldCheck, FileText, RotateCcw } from "lucide-react";
import DOMPurify from "dompurify";
import { useLang } from "../context/LanguageContext";
import { legalDefaults } from "../config/legalDefaults";
import api from "../api";

const Section = ({ title, body }) => (
  <div className="mb-6">
    <h2 className="text-lg font-bold text-foreground mb-2">{title}</h2>
    <div className="text-sm text-muted-foreground leading-relaxed space-y-2">
      {String(body || "").split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
    </div>
  </div>
);

export const LegalPage = ({ kind }) => {
  const { t, lang } = useLang();
  const [custom, setCustom] = useState(null);
  const isTerms = kind === "terms";
  const isRefunds = kind === "refunds";

  useEffect(() => {
    api.get(`/legal/${kind}?lang=${lang || "en"}`).then(({ data }) => setCustom(data)).catch(() => setCustom({}));
  }, [kind, lang]);

  const base = legalDefaults(kind, t);
  const c = custom || {};
  const updated = c.updated || "September 2026";
  const intro = c.intro || base.intro;
  const sections = c.replace && c.sections?.length
    ? c.sections
    : [...base.sections, ...(c.sections || [])];

  const heroIcon = isRefunds ? <RotateCcw size={13} /> : isTerms ? <FileText size={13} /> : <ShieldCheck size={13} />;
  const heroTag = isRefunds ? "Billing" : isTerms ? "Legal" : "Your privacy";
  const title = isRefunds ? t("lg.refundPolicy") : isTerms ? t("lg.termsOfUse") : t("lg.privacyPolicy");

  return (
    <div className="max-w-3xl lp-fade-up" data-testid={isRefunds ? "refunds-page" : isTerms ? "terms-page" : "privacy-page"}>
      <div className="relative overflow-hidden rounded-3xl bg-[#0f2a1a] text-white p-7 sm:p-9 mb-6">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#16a34a]/40 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-emerald-200 backdrop-blur">
            {heroIcon} {heroTag}
          </span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">{title}</h1>
          <p className="mt-2 text-sm text-emerald-100/80" data-testid="legal-updated-label">Last updated: {updated}</p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 sm:p-8">
        {c.html ? (
          <div className="lp-rich text-sm text-muted-foreground" data-testid="legal-rich-body"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.html) }} />
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-6" data-testid="legal-intro-text">{intro}</p>
            {sections.map((s, i) => (
              <div key={i} data-testid={`legal-section-view-${i}`}>
                <Section title={s.title} body={s.body} />
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default LegalPage;
